import { navigate } from '@reach/router';
import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { Checkbox } from '../../shared/form/inputs/Checkbox';
import { colourGreen02, colourNeutral07, colourPrimarySeaGreen } from '../../styling/colours';
import { DeleteDocumentDto } from './deleteDocumentDto';
import { DeleteDocumentWarningModal } from './DeleteDocumentWarningModal';
import { ErrorBox } from '../../shared/ErrorBox';
import { Expandable } from '../../shared/Expandable';
import { fontSize03 } from '../../styling/fonts';
import { getCopyDocumentUrl, getUpdateDocumentUrl } from './documents-section/documentRoutes';
import { HandoverPackSectionResponse } from './documentResponse';
import { MenuButton, MenuButtonItem } from '../../shared/MenuButton';
import { spacing01, spacing02, spacing03, spacing06, spacing07 } from '../../styling/spacing';
import { useApiRequest } from '../../api/useApiRequest';

type Props = {
  document: DocumentProps;
  onSuccess?: () => void;
  showMenu?: boolean;
};

type DocumentProps = {
  documentId: number;
  documentReference: string;
  documentName: string;
  customerName: string;
  documentUrl: string;
  documentVersion: number | null;
  handoverPackSection: HandoverPackSectionResponse | null;
  handoverPackSectionSequence: string;
  isAlwaysIncluded: boolean;
  useNuHeatSuppliedHeatPump: boolean;
  useSolar: boolean;
  useUnderfloorHeating: boolean;
  useUserSuppliedHeatPump: boolean;
  useBoiler: boolean;
  useAnyTechnology: boolean;
  matchAllParts: boolean;
  partNumbers: Array<string>;
  secondaryPartNumbers: Array<string>;
  includeInContentsPage: boolean;
  includeInPrintFolder: boolean;
  prePrinted: boolean;
  neverPrint: boolean;
};

export const DocumentDetails = ({ document, onSuccess, showMenu }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { error, sendRequest } = useApiRequest<undefined, DeleteDocumentDto>({
    endpointUrl: 'documents/delete',
    httpMethod: 'delete',
  });

  const handleDelete = () => {
    void sendRequest({
      requestBody: { documentId: document.documentId },
      onSuccess: async () => {
        onSuccess?.();
      },
    });
  };

  return (
    <DocumentContainer>
      <DocumentHeader expanded={isExpanded}>
        <BasicDetails onClick={() => setIsExpanded(!isExpanded)}>
          <p>{document.documentReference}</p>
          <p>{document.documentName}</p>
          <p>{document.handoverPackSectionSequence}</p>
          <NavLink href={document.documentUrl}>
            <p>Open document</p>
          </NavLink>
        </BasicDetails>
        {showMenu && (
          <div style={{ maxWidth: spacing07 }}>
            <MenuButton>
              <MenuButtonItem onClick={() => navigate(getUpdateDocumentUrl(document.documentId))}>Edit</MenuButtonItem>
              <MenuButtonItem onClick={() => navigate(getCopyDocumentUrl(document.documentId))}>Copy</MenuButtonItem>
              <MenuButtonItem onClick={() => setIsDeleteModalOpen(true)}>Delete</MenuButtonItem>
            </MenuButton>
          </div>
        )}
      </DocumentHeader>
      <Expandable isExpanded={isExpanded}>
        <DocumentGrid showMenu={showMenu}>
          <div>
            <DetailsTitle>Customer Name</DetailsTitle>
            <DetailsText>{document.customerName}</DetailsText>
          </div>
          <div>
            <DetailsTitle>Version</DetailsTitle>
            <DetailsText>{document.documentVersion}</DetailsText>
          </div>
          {/* Add two empty grids for formatting purposes */}
          <div />
          <div />
          <div>
            <DetailsTitle>Include in Contents Page</DetailsTitle>
            <Checkbox checked={document.includeInContentsPage} disabled />
          </div>
          <div>
            <DetailsTitle>Include in Print Folder</DetailsTitle>
            <Checkbox checked={document.includeInPrintFolder} disabled />
          </div>
          <div>
            <DetailsTitle>Pre Printed</DetailsTitle>
            <Checkbox checked={document.prePrinted} disabled />
          </div>
          <div>
            <DetailsTitle>Never Print</DetailsTitle>
            <Checkbox checked={document.neverPrint} disabled />
          </div>
          <div>
            <DetailsTitle>Nu-Heat Supplied Heat Pump</DetailsTitle>
            <Checkbox checked={document.useNuHeatSuppliedHeatPump} disabled />
          </div>
          <div>
            <DetailsTitle>Solar</DetailsTitle>
            <Checkbox checked={document.useSolar} disabled />
          </div>
          <div>
            <DetailsTitle>Underfloor Heating</DetailsTitle>
            <Checkbox checked={document.useUnderfloorHeating} disabled />
          </div>
          <div>
            <DetailsTitle>User Supplied Heat Pump</DetailsTitle>
            <Checkbox checked={document.useUserSuppliedHeatPump} disabled />
          </div>
          <div>
            <DetailsTitle>Boiler</DetailsTitle>
            <Checkbox checked={document.useBoiler} disabled />
          </div>
          <div>
            <DetailsTitle>Any</DetailsTitle>
            <Checkbox checked={document.useAnyTechnology} disabled />
          </div>
          <div>
            <DetailsTitle>Always Included</DetailsTitle>
            <Checkbox checked={document.isAlwaysIncluded} disabled />
          </div>
          <div>
            <DetailsTitle>Part Numbers</DetailsTitle>
            <DetailsText>
              {document.matchAllParts
                ? 'Matching all parts'
                : document.partNumbers.length > 0
                ? document.partNumbers.join(', ')
                : 'None'}
            </DetailsText>
          </div>
          <div>
            <DetailsTitle>Secondary Part Numbers</DetailsTitle>
            <DetailsText>
              {document.matchAllParts
                ? 'Matching all parts'
                : document.secondaryPartNumbers.length > 0
                ? document.secondaryPartNumbers.join(', ')
                : 'None'}
            </DetailsText>
          </div>
        </DocumentGrid>
      </Expandable>
      {error && <ErrorBox error={error} />}
      <DeleteDocumentWarningModal
        handleDelete={handleDelete}
        isOpen={isDeleteModalOpen}
        closeModal={() => setIsDeleteModalOpen(false)}
        document={document}
      />
    </DocumentContainer>
  );
};

const DocumentContainer = styled.div`
  margin-top: ${spacing03};
  background-color: ${colourNeutral07};
  border-radius: 0.25rem;
  padding: ${spacing02};
  text-align: center;
`;

const DocumentGrid = styled.div<{ showMenu?: boolean }>`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-right: ${props => props.showMenu && spacing07};
`;

const DocumentHeader = styled.div<{ expanded: boolean }>`
  display: flex;
  border-bottom: ${props => (props.expanded ? '1px solid dimgray' : 'none')};
  align-items: center;
`;

const BasicDetails = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  font-size: ${fontSize03};
  font-weight: bold;
  cursor: pointer;
  align-items: center;
  justify-items: center;
`;

const NavLink = styled.a`
  color: ${colourPrimarySeaGreen};
  min-width: ${spacing06};
  min-height: ${spacing06};
  border: solid 1px ${colourGreen02};
  border-radius: 5px;
  padding-left: ${spacing01};
  padding-right: ${spacing01};
`;

const DetailsTitle = styled.p`
  font-weight: bold;
  margin-bottom: 0;
`;

const DetailsText = styled.p`
  margin-top: 0;
`;
